import {
  differenceInCalendarDays,
  format,
  parseISO,
  startOfDay,
} from 'date-fns'
import maxBy from 'lodash-es/maxBy'

import { EMPTY_LIST, EMPTY_STRING } from '../../constants/common'
import {
  DATE_API_DATA_FORMAT_SHORT,
  DATE_SHORT_FORMAT,
} from '../../constants/date'
import { ROUTE_PATH_LIST_RENTAL_PORTAL } from '../../constants/route'
import {
  getSectionCanViewPermission,
  isValueStringType,
} from '../../utils/common'
import { toUkTimezoneDate, transposeToUkTimezoneDate } from '../../utils/date'
import { downloadFileByUrl } from '../../utils/file'
import {
  getListingPortalLabel,
  getListingPortalLogoUrl,
} from '../RentalPortalListing'
import { formatDate } from '../Table'

import {
  PORTAL_AVATAR_TEXT,
  RENTAL_PORTAL_CSV_BOM,
  RENTAL_PORTAL_CSV_LINE_BREAK,
  RENTAL_PORTAL_CSV_MIME,
  RENTAL_PORTAL_CSV_SEPARATOR,
  RENTAL_PORTAL_SUBSECTION_LIST,
} from './RentalPortal.constants'

function toRentalPortalCsvCell(value) {
  return `"${String(value ?? EMPTY_STRING).replaceAll('"', '""')}"`
}

function getIsRentalPortalSubsectionPathname({ pathname, subsection }) {
  const link = getRentalPortalSubsectionLink(subsection.name)

  const isSubsectionPage = pathname === link
  const isSubsectionNestedPage = pathname.startsWith(`${link}/`)

  return isSubsectionPage || isSubsectionNestedPage
}

export function getRentalPortalSubsectionLink(subsectionName) {
  return `/${ROUTE_PATH_LIST_RENTAL_PORTAL.root}/${subsectionName}`
}

export function getRentalPortalViewableSubsections(sections) {
  return RENTAL_PORTAL_SUBSECTION_LIST.filter(subsection =>
    getSectionCanViewPermission(sections, subsection.uiSectionName)
  )
}

export function getRentalPortalDefaultSubsectionName(sections) {
  return (
    getRentalPortalViewableSubsections(sections)[0] ||
    RENTAL_PORTAL_SUBSECTION_LIST[0]
  ).name
}

export function getRentalPortalSubsectionByPathname(pathname) {
  return maxBy(
    RENTAL_PORTAL_SUBSECTION_LIST.filter(subsection =>
      getIsRentalPortalSubsectionPathname({ pathname, subsection })
    ),
    subsection => subsection.name.length
  )
}

export function getIsRentalPortalSubsectionActive({
  activeSubsection,
  subsection,
}) {
  return subsection.name === activeSubsection?.name
}

export function getIsListingAvailableImmediately({ availableFrom, timezone }) {
  return (
    !availableFrom ||
    differenceInCalendarDays(
      parseListingDayInAppTimezone(availableFrom, timezone),
      toUkTimezoneDate(new Date(), timezone)
    ) <= 0
  )
}

export function parseListingFormDate(value, timezone) {
  if (!value) {
    return null
  }

  if (!isValueStringType(value)) {
    return value
  }

  return timezone ? toUkTimezoneDate(value, timezone) : parseISO(value)
}

export function parseListingDayInAppTimezone(value, timezone) {
  const date = parseListingFormDate(value)

  return date ? transposeToUkTimezoneDate(startOfDay(date), timezone) : date
}

export function getListingDateValue(value) {
  return value ? format(value, DATE_API_DATA_FORMAT_SHORT) : null
}

export function getRentalPortalBoardSelectionState({
  getIsSelectable,
  items,
  selectedIds,
}) {
  const selectableItems = items.filter(getIsSelectable)

  const selectedCount = selectableItems.filter(item =>
    selectedIds.includes(item.id)
  ).length

  return {
    isAllSelected:
      selectableItems.length > 0 && selectedCount === selectableItems.length,
    isPartlySelected:
      selectedCount > 0 && selectedCount < selectableItems.length,
    selectableCount: selectableItems.length,
    selectableItems,
  }
}

export function getRentalPortalBoardNextSelection({
  isChecked,
  items,
  selected,
}) {
  const itemIds = items.map(item => item.id)

  const rest = selected.filter(item => !itemIds.includes(item.id))

  return isChecked ? [...rest, ...items] : rest
}

export function getPortalAvatarText(portal) {
  return PORTAL_AVATAR_TEXT[portal] ?? portal
}

export function getPortalAvatarProps(portal) {
  const label = getListingPortalLabel(portal)

  return {
    alt: label,
    src: getListingPortalLogoUrl(portal),
    text: getPortalAvatarText(label),
  }
}

export function getListingProfileTabsWithAttentionDot({
  dotClassName,
  preparedTabs,
  tabsAwaitingYou = EMPTY_LIST,
  tooltip,
}) {
  return preparedTabs.map(tab =>
    tabsAwaitingYou.includes(tab.id)
      ? {
          ...tab,
          text: (
            <>
              {tab.text}
              <span className={dotClassName} />
            </>
          ),
          tooltip,
        }
      : tab
  )
}

export function getRentalPortalExportItems({
  checkIsItemSelected,
  items,
  selectAllPages,
  selected,
}) {
  return selectAllPages || selected.length > 0
    ? items.filter(checkIsItemSelected)
    : items
}

export function getRentalPortalExportColumns(columns) {
  return Object.values(columns)
    .filter(column => column.visible)
    .sort((left, right) => left.index - right.index)
}

export function getRentalPortalExportRows({
  columns,
  getValue,
  items,
  locale,
  t,
  timezone,
}) {
  return [
    columns.map(column => t(column.title)),
    ...items.map(item =>
      columns.map(column =>
        getValue({ item, locale, name: column.name, t, timezone })
      )
    ),
  ]
}

export function getRentalPortalExportDate({ locale, timezone, value }) {
  return value
    ? formatDate({
        date: value,
        dateFormat: DATE_SHORT_FORMAT,
        locale,
        timezone,
        useTodayFormat: false,
      })
    : null
}

export function getRentalPortalExportFileName(section) {
  return `${section}-${format(new Date(), DATE_API_DATA_FORMAT_SHORT)}.csv`
}

// TODO: compose the file server-side once an RP export endpoint exists
export function downloadRentalPortalCsv({ fileName, rows }) {
  const url = URL.createObjectURL(
    new Blob(
      [
        RENTAL_PORTAL_CSV_BOM,
        rows
          .map(row =>
            row.map(toRentalPortalCsvCell).join(RENTAL_PORTAL_CSV_SEPARATOR)
          )
          .join(RENTAL_PORTAL_CSV_LINE_BREAK),
      ],
      { type: RENTAL_PORTAL_CSV_MIME }
    )
  )

  downloadFileByUrl(url, fileName)

  URL.revokeObjectURL(url)
}

export function getRentalPortalSectionSubsections(subsections) {
  return subsections.filter(subsection => !subsection.isInSettingsAccordion)
}

export function getRentalPortalSettingsSubsections(subsections) {
  return subsections.filter(subsection => subsection.isInSettingsAccordion)
}

export function getHasRentalPortalSettingsSubsections(settingsSubsections) {
  return settingsSubsections.length > 0
}
